import { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Dropdown } from '@/components/ui/select'
import { apiClient } from '@/lib/api-client'
import { toast } from 'react-hot-toast'

interface User {
  id: number
  name: string
  email: string
  role: 'Admin' | 'Employee'
  profileImage?: string
  employeeId?: string
}

interface UserFormProps {
  isOpen: boolean
  onClose: () => void
  user?: User | null
  onSuccess: () => void
}

interface FormData {
  name: string
  email: string
  password: string
  role: 'Admin' | 'Employee'
  employeeId?: string
}

export function UserForm({ isOpen, onClose, user, onSuccess }: UserFormProps) {
  const [loading, setLoading] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors }
  } = useForm<FormData>()

  useEffect(() => {
    if (isOpen) {
      if (user) {
        reset({
          name: user.name,
          email: user.email,
          password: '', // Don't pre-fill password for existing users
          role: user.role,
          employeeId: user.employeeId || ''
        })
      } else {
        reset({
          name: '',
          email: '',
          password: '',
          role: 'Employee',
          employeeId: ''
        })
      }
    }
  }, [isOpen, user, reset])

  const onSubmit = async (data: FormData) => {
    setLoading(true)
    try {
      const userData = {
        ...data,
        employeeId: data.employeeId?.trim() || null,
      }
      
      // Don't send empty password for existing users
      if (user && !data.password) {
        delete (userData as any).password
      }

      if (user) {
        await apiClient.put(`/users/${user.id}`, userData)
        toast.success('User updated successfully')
      } else {
        await apiClient.post('/users', userData)
        toast.success('User created successfully')
      }
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Error saving user:', error)
      toast.error(error.response?.data?.message || 'Failed to save user')
    } finally {
      setLoading(false)
    }
  }

  const roleOptions = [
    { value: 'Admin', label: 'Admin' },
    { value: 'Employee', label: 'Employee' }
  ]

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={user ? 'Edit User' : 'Create New User'}
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="text-sm font-medium text-foreground required">
            Full Name
          </label>
          <Input
            {...register('name', { required: 'Name is required' })}
            placeholder="Enter full name"
            className={errors.name ? 'border-destructive' : ''}
          />
          {errors.name && (
            <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-foreground required">
            Email
          </label>
          <Input
            type="email"
            {...register('email', { 
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            })}
            placeholder="Enter email address"
            className={errors.email ? 'border-destructive' : ''}
          />
          {errors.email && (
            <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-foreground">
            Employee ID
          </label>
          <Input
            {...register('employeeId', {
              maxLength: {
                value: 50,
                message: 'Employee ID must be less than 50 characters'
              }
            })}
            placeholder="Enter employee ID (optional)"
            className={errors.employeeId ? 'border-destructive' : ''}
          />
          {errors.employeeId && (
            <p className="text-sm text-destructive mt-1">{errors.employeeId.message}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            Optional unique identifier for the employee
          </p>
        </div>

        <div>
          <label className={`text-sm font-medium text-foreground${user ? '' : ' required'}`}>
            Password
          </label>
          <Input
            type="password"
            {...register('password', {
              validate: (value) => {
                if (!value) return user ? true : 'Password is required'
                if (value.length < 6) return 'Password must be at least 6 characters'
                return true
              },
            })}
            placeholder={user ? 'Leave blank to keep current password' : 'Enter password'}
            className={errors.password ? 'border-destructive' : ''}
          />
          {errors.password && (
            <p className="text-sm text-destructive mt-1">{errors.password.message}</p>
          )}
          {user && (
            <p className="text-xs text-muted-foreground mt-1">
              Leave blank to keep current password
            </p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-foreground required">
            Role
          </label>
          <Controller
            name="role"
            control={control}
            rules={{ required: 'Role is required' }}
            render={({ field }) => (
              <Dropdown
                value={field.value || ''}
                placeholder="Select a role"
                onChange={field.onChange}
                options={roleOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
              />
            )}
          />
          {errors.role && (
            <p className="text-sm text-destructive mt-1">{errors.role.message}</p>
          )}
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Saving...' : user ? 'Update User' : 'Create User'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
